import React, { useState, useEffect } from 'react';
import {
    AppBar,
    InputBase,
    Toolbar,
    IconButton,
    Button,
    Typography,
    Box,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ClickAwayListener,
    useTheme,
    useMediaQuery,
    styled,
    Fade
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { getAuthToken, handleLogout } from "../common/token-utils";
import { useThemeToggle } from './theme-context';
import { getCreatorProfile, getAudienceProfile } from "../service/content-service";

// Keyframes for shimmer effect
const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
    boxShadow: 'none',
    transition: 'all 0.3s ease-in-out',
}));

const Logo = styled(Typography)(({ theme }) => ({
    fontFamily: "'Edu AU VIC WA NT Hand', cursive",
    fontSize: '2.5rem',
    color: '#ffffff',
    textDecoration: 'none',
    position: 'relative',
    display: 'inline-block',
    textShadow: '0 0 10px rgba(255,255,255,0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.05)',
        textShadow: '0 0 15px rgba(255,255,255,0.5)',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '2px',
        background: 'rgba(255,255,255,0.3)',
        transform: 'scaleX(0)',
        transformOrigin: 'right',
        transition: 'transform 0.3s ease-in-out',
    },
    '&:hover::after': {
        transform: 'scaleX(1)',
        transformOrigin: 'left',
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    fontFamily: 'DM Sans',
    fontWeight: '600',
    fontSize: '14px',
    padding: '10px 25px',
    color: '#fff',
    borderRadius: '0',
    border: '1px solid rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(61, 63, 73, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        backgroundColor: 'rgba(91, 93, 104, 0.4)',
        borderColor: 'rgba(255,255,255,0.4)',
        transform: 'translateY(-2px)',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.1), transparent)',
        animation: `${shimmer} 1.5s infinite`,
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
    },
    '&:hover::before': {
        opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
        padding: '8px 20px',
    },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    maxWidth: theme.breakpoints.values.xl,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5, 2),
    },
}));

const StyledSearchBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    padding: '4px 8px',
    margin: '0 16px',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: '8px',
        transition: theme.transitions.create('width'),
        width: '100%',
        color: '#fff',
        '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.7)',
            opacity: 1,
        },
    },
}));

function CustomAppBar({ hideSearch = true, isCreator = false }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();
    const accessToken = getAuthToken();
    const theme = useTheme();
    const { toggleColorMode } = useThemeToggle();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        setIsVisible(true);
        const fetchUserProfile = async () => {
            if (accessToken) {
                setLoading(true);
                try {
                    let response;

                    if (isCreator) {
                        response = await getCreatorProfile(accessToken);
                    } else {
                        response = await getAudienceProfile(accessToken);
                    }

                    if (response?.data) {
                        setUserProfile(response.data);
                    }
                } catch (err) {
                    console.error(`Error fetching ${isCreator ? 'Creator' : 'Audience'} profile:`, err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchUserProfile();
    }, [accessToken, isCreator]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogin = () => {
        navigate("/login");
    };

    const menuItems = [
        { text: 'Logout', icon: <ExitToAppIcon />, action: handleLogout },
    ];

    if (isCreator) {
        menuItems.unshift(
            { text: 'My Events', icon: <EventIcon />, action: () => navigate('/creator/MyEvents') },
            { text: 'My Profile', icon: <PersonIcon />, action: () => navigate('/profile') }
        );
    } else {
        menuItems.unshift(
            { text: 'My Tickets', icon: <ConfirmationNumberIcon />, action: () => navigate('/myTickets') },
            { text: 'My Profile', icon: <PersonIcon />, action: () => navigate('/profile') }
        );
    }

    const menuList = () => (
        <List>
            {userProfile && (
                <ListItem
                    sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.1)',
                        pointerEvents: 'none',
                        padding: '12px 16px'
                    }}
                >
                    <ListItemText
                        primary={userProfile.name || 'User'}
                        secondary={userProfile.email}
                        primaryTypographyProps={{
                            noWrap: true
                        }}
                        secondaryTypographyProps={{
                            noWrap: true
                        }}
                    />
                </ListItem>
            )}
            {menuItems.map((item) => (
                <ListItem
                    button
                    key={item.text}
                    onClick={() => {
                        handleClose();
                        item.action();
                    }}
                >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                </ListItem>
            ))}
        </List>
    );

    const renderSearchSection = () => {
        if (hideSearch) return null;
        return (
            <StyledSearchBox sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                <StyledInputBase placeholder="Search events" />
                <LocationOnIcon sx={{ color: 'rgba(255,255,255,0.7)', ml: 2 }} />
                <StyledInputBase placeholder="Tualatin" />
                <StyledButton sx={{ ml: 2 }}>Search</StyledButton>
            </StyledSearchBox>
        );
    };

    const renderAuthenticatedIcons = () => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 }
        }}>
            {isCreator && (
                <StyledButton
                    onClick={() => navigate("/event/new")}
                    disableRipple
                >
                    Create New Event
                </StyledButton>
            )}
            <Box
                sx={{
                    cursor: 'pointer'
                }}
                onClick={handleClick}
            >
                <AccountCircleIcon sx={{
                    fontSize: { xs: 24, sm: 28, md: 32 },
                    color: '#fff'
                }} />
            </Box>
        </Box>
    );

    const renderLoginIcon = () => (
        <StyledButton
            onClick={handleLogin}
            disableRipple
        >
            Login
        </StyledButton>
    );

    return (
        <StyledAppBar position="static">
            <Fade in={isVisible} timeout={1000}>
                <StyledToolbar>
                    <Box component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
                        <Logo variant={isMobile ? "h5" : "h4"} component="span">
                            kalakaar
                        </Logo>
                    </Box>

                    {renderSearchSection()}

                    {accessToken ? renderAuthenticatedIcons() : renderLoginIcon()}

                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                width: { xs: '200px', sm: '250px' },
                                '& .MuiList-root': {
                                    padding: 0,
                                },
                                '& .MuiListItem-root': {
                                    padding: '12px 16px',
                                },
                            }
                        }}
                    >
                        <ClickAwayListener onClickAway={handleClose}>
                            <div>{menuList()}</div>
                        </ClickAwayListener>
                    </Popover>
                </StyledToolbar>
            </Fade>
        </StyledAppBar>
    );
}

export default CustomAppBar;